import {useCallback} from 'react';

import NiceModal, {useModal} from '@ebay/nice-modal-react';
import {Observer} from 'mobx-react-lite';

import type {TextInputField} from '@yourcoach/shared/utils/validation/createTextInputField';

import {i18n} from '@yourcoach/i18n';
import {Form} from '@yourcoach/shared/components/Form';
import {CRUProgramScheduleConferenceStore} from '@yourcoach/shared/modules/program/cru/schedule';
import {DurationTextInput} from '@yourcoach/shared/uikit/DurationTextInput';
import {useMobxStore} from '@yourcoach/shared/utils/store';
import {TestID} from '@yourcoach/shared/utils/test-utils/data-test-id';

import type {Props} from './types';

import {
  Container,
  List,
  ModalConference,
  useCRUScheduleEvent,
} from '../useCRUScheduleEvent';

export const CRUProgramScheduleConferenceModal = NiceModal.create<Props>(
  ({conference, program}) => {
    const modal = useModal();

    const store = useMobxStore(
      () => new CRUProgramScheduleConferenceStore({conference, program}),
    );

    const onRequestClose = useCallback(() => {
      modal.resolve();
      modal.hide();
    }, [modal]);

    const onSave = useCallback(() => {
      const conferenceToSave = store.getConference();

      if (conferenceToSave) {
        modal.resolve(conferenceToSave);
        modal.hide();
      } else {
        Object.keys(store.fields)
          .map(key => store.fields[key])
          .some((field: TextInputField) => {
            if (!field.isValid) {
              field.ref.current?.focus();

              return true;
            }

            return false;
          });
      }
    }, [modal, store]);

    const {
      daySection,
      descriptionSection,
      footer,
      repeatSection,
      timeSection,
      titleSection,
    } = useCRUScheduleEvent(store, onSave, 'conference');

    return (
      <ModalConference
        bodyClassName="body"
        isOpen={modal.visible}
        onAfterClose={modal.remove}
        onRequestClose={onRequestClose}
        title={store.title}
      >
        <Container>
          <List customScrollbar>
            {titleSection}
            <Form.Row>
              {repeatSection}
              {daySection}
            </Form.Row>
            <Form.Row>
              {timeSection}
              <Form.Section>
                <Form.Label>
                  {i18n.t('shared:CRUProgramScheduleConference.duration_label')}
                </Form.Label>
                <Observer>
                  {() => (
                    <DurationTextInput
                      hoursValue={store.durationTime[0]!.toString().padStart(
                        2,
                        '0',
                      )}
                      minutesValue={store.durationTime[1]!.toString().padStart(
                        2,
                        '0',
                      )}
                      onChange={store.onDurationChange}
                      testID={TestID.schedule_event.input.duration}
                    />
                  )}
                </Observer>
              </Form.Section>
            </Form.Row>
            {descriptionSection}
          </List>
        </Container>
        {footer}
      </ModalConference>
    );
  },
);
